import type { TrafficInspectorEdge, TrafficInspectorNode } from '../types/trafficInspector';

export type TrafficTopology = {
  nodes: TrafficInspectorNode[];
  edges: TrafficInspectorEdge[];
};

export function topologySignature(topology: TrafficTopology | null | undefined): string {
  if (!topology) return '';
  const nodeIds = (topology.nodes ?? []).map((node) => node.id).filter(Boolean).sort();
  const edgeIds = (topology.edges ?? [])
    .map((edge) => edge.edgeId ?? `${edge.source}->${edge.target}`)
    .filter(Boolean)
    .sort();
  return `${nodeIds.join('|')}::${edgeIds.join('|')}`;
}

export function isValidTopology(topology: unknown): topology is TrafficTopology {
  if (!topology || typeof topology !== 'object') return false;
  const candidate = topology as Partial<TrafficTopology>;
  if (!Array.isArray(candidate.nodes) || !Array.isArray(candidate.edges)) return false;
  if (candidate.nodes.length === 0) return false;

  const nodeIds = new Set<string>();
  for (const node of candidate.nodes) {
    if (!node || typeof node !== 'object') return false;
    const id = (node as TrafficInspectorNode).id;
    if (!id || typeof id !== 'string' || !id.trim()) return false;
    nodeIds.add(id);
  }

  for (const edge of candidate.edges) {
    if (!edge || typeof edge !== 'object') return false;
    const { source, target } = edge as TrafficInspectorEdge;
    if (!source || !target) return false;
    if (!nodeIds.has(source) || !nodeIds.has(target)) return false;
  }

  return true;
}

export function structuralLayoutSignature(
  nodeIds: string[],
  edges: Array<{ source: string; target: string }>,
  options?: { groupBands?: boolean }
): string {
  const nodes = [...nodeIds].filter(Boolean).sort().join('|');
  const edgeKeys = edges.map((edge) => `${edge.source}->${edge.target}`).filter(Boolean).sort().join('|');
  return `${nodes}::${edgeKeys}::${options?.groupBands ? 'bands' : 'plain'}`;
}

export type TopologySnapshotState = {
  data: TrafficTopology | null;
  signature: string;
  isRefreshing: boolean;
  lastUpdatedAt: number | null;
  error: string | null;
};

export function mergeTopologySnapshot(
  prev: TopologySnapshotState,
  next: unknown,
  options?: { isRefreshing?: boolean; error?: string | null }
): TopologySnapshotState {
  const isRefreshing = options?.isRefreshing ?? false;
  const error = options?.error ?? null;

  if (!isValidTopology(next)) {
    return {
      ...prev,
      isRefreshing,
      error: error ?? (isRefreshing ? prev.error : 'Topology refresh returned invalid or empty data.'),
    };
  }

  const nextSig = topologySignature(next);
  const prevSig = topologySignature(prev.data);

  if (prevSig === nextSig && prev.data) {
    return {
      data: next,
      signature: nextSig,
      isRefreshing: false,
      lastUpdatedAt: Date.now(),
      error: null,
    };
  }

  return {
    data: next,
    signature: nextSig,
    isRefreshing: false,
    lastUpdatedAt: Date.now(),
    error: null,
  };
}

/** Keep the last known good topology when a poll returns invalid or empty data. */
export function acceptTopologyUpdate(
  current: TrafficTopology | null,
  next: unknown
): TrafficTopology | null {
  if (!isValidTopology(next)) {
    if (current && isValidTopology(current)) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[TrafficInspector] Ignoring invalid topology payload; keeping last good snapshot.');
      }
      return current;
    }
    return current;
  }
  return next;
}
